import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { getCategoryByName, getCategoryRGB } from '../constants/ticketCategories';

export interface TicketData {
  ticketNumber: string;
  eventTitle: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
  eventVenue: string;
  category: string;
  ticketType: string;
  price: number;
  customerName: string;
  customerPhone: string;
  seatInfo?: string;
  qrCode: string;
}

/**
 * Generate a PDF ticket with QR code
 */
export async function generateTicketPDF(ticket: TicketData): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [210, 297] // A4 size
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);

  // Background gradient effect (light purple)
  doc.setFillColor(124, 77, 255);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // White content area
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, margin, contentWidth, pageHeight - (margin * 2), 5, 5, 'F');

  let yPos = margin + 20;

  // Header - Event Title
  doc.setFontSize(24);
  doc.setTextColor(124, 77, 255);
  doc.setFont('helvetica', 'bold');
  const titleLines = doc.splitTextToSize(ticket.eventTitle, contentWidth - 20);
  doc.text(titleLines, margin + 10, yPos);
  yPos += titleLines.length * 10 + 10;

  // Category Badge - Use ticketType (category) with its color
  const ticketCategory = getCategoryByName(ticket.ticketType);
  const categoryColor = ticketCategory ? getCategoryRGB(ticket.ticketType) : [124, 77, 255];
  const categoryName = ticketCategory ? ticketCategory.name : ticket.ticketType;
  
  doc.setFillColor(categoryColor[0], categoryColor[1], categoryColor[2]);
  const badgeWidth = Math.max(50, categoryName.length * 4 + 10);
  doc.roundedRect(margin + 10, yPos - 8, badgeWidth, 8, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(categoryName.toUpperCase(), margin + 12, yPos - 2);
  yPos += 15;

  // Event Details
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  
  // Date
  doc.setFont('helvetica', 'bold');
  doc.text('Date:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(ticket.eventDate, margin + 30, yPos);
  yPos += 8;

  // Time
  doc.setFont('helvetica', 'bold');
  doc.text('Time:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(ticket.eventTime, margin + 30, yPos);
  yPos += 8;

  // Location
  doc.setFont('helvetica', 'bold');
  doc.text('Location:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  const locationLines = doc.splitTextToSize(ticket.eventLocation, contentWidth - 50);
  doc.text(locationLines, margin + 30, yPos);
  yPos += locationLines.length * 8 + 5;

  // Venue
  if (ticket.eventVenue) {
    doc.setFont('helvetica', 'bold');
    doc.text('Venue:', margin + 10, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(ticket.eventVenue, margin + 30, yPos);
    yPos += 8;
  }

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
  yPos += 10;

  // Ticket Information
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(124, 77, 255);
  doc.text('TICKET INFORMATION', margin + 10, yPos);
  yPos += 10;

  doc.setFontSize(11);
  doc.setTextColor(50, 50, 50);
  
  // Ticket Number
  doc.setFont('helvetica', 'bold');
  doc.text('Ticket Number:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(ticket.ticketNumber, margin + 50, yPos);
  yPos += 8;

  // Ticket Type (Category)
  doc.setFont('helvetica', 'bold');
  doc.text('Category:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(categoryColor[0], categoryColor[1], categoryColor[2]);
  doc.setFont('helvetica', 'bold');
  doc.text(categoryName, margin + 40, yPos);
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'normal');
  yPos += 8;

  // Price
  doc.setFont('helvetica', 'bold');
  doc.text('Price:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`$${ticket.price.toFixed(2)}`, margin + 30, yPos);
  yPos += 8;

  // Seat Info (if available)
  if (ticket.seatInfo) {
    doc.setFont('helvetica', 'bold');
    doc.text('Seat:', margin + 10, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(ticket.seatInfo, margin + 30, yPos);
    yPos += 8;
  }

  yPos += 5;

  // Customer Information
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(124, 77, 255);
  doc.text('CUSTOMER INFORMATION', margin + 10, yPos);
  yPos += 10;

  doc.setFontSize(11);
  doc.setTextColor(50, 50, 50);
  
  // Customer Name
  doc.setFont('helvetica', 'bold');
  doc.text('Name:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(ticket.customerName, margin + 30, yPos);
  yPos += 8;

  // Customer Phone
  doc.setFont('helvetica', 'bold');
  doc.text('Phone:', margin + 10, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(ticket.customerPhone, margin + 30, yPos);
  yPos += 15;

  // QR Code
  try {
    const qrCodeDataURL = await QRCode.toDataURL(ticket.qrCode, {
      width: 80,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });

    const qrSize = 50;
    const qrX = (pageWidth - qrSize) / 2;
    const qrY = yPos;

    doc.addImage(qrCodeDataURL, 'PNG', qrX, qrY, qrSize, qrSize);
    
    // QR Code label
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('Scan for verification', qrX, qrY + qrSize + 5, { align: 'center' });
  } catch (error) {
    console.error('Error generating QR code:', error);
    // Fallback: just show ticket number
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Ticket: ${ticket.ticketNumber}`, margin + 10, yPos + 20);
  }

  // Footer
  const footerY = pageHeight - margin - 10;
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont('helvetica', 'italic');
  doc.text('This ticket is valid only for the event specified above.', margin + 10, footerY);
  doc.text('Please present this ticket at the venue entrance.', margin + 10, footerY + 5);

  // Generate PDF blob
  const pdfBlob = doc.output('blob');
  return pdfBlob;
}

/**
 * Generate multiple tickets as a single PDF
 */
export async function generateMultipleTicketsPDF(tickets: TicketData[]): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [210, 297]
  });

  for (let i = 0; i < tickets.length; i++) {
    if (i > 0) {
      doc.addPage();
    }

    const ticket = tickets[i];
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    // Background
    doc.setFillColor(124, 77, 255);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // White content area
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, margin, contentWidth, pageHeight - (margin * 2), 5, 5, 'F');

    let yPos = margin + 20;

    // Header
    doc.setFontSize(24);
    doc.setTextColor(124, 77, 255);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(ticket.eventTitle, contentWidth - 20);
    doc.text(titleLines, margin + 10, yPos);
    yPos += titleLines.length * 10 + 10;

    // Category Badge - Use ticketType (category) with its color
    const ticketCategory = getCategoryByName(ticket.ticketType);
    const categoryColor = ticketCategory ? getCategoryRGB(ticket.ticketType) : [124, 77, 255];
    const categoryName = ticketCategory ? ticketCategory.name : ticket.ticketType;
    
    doc.setFillColor(categoryColor[0], categoryColor[1], categoryColor[2]);
    const badgeWidth = Math.max(50, categoryName.length * 4 + 10);
    doc.roundedRect(margin + 10, yPos - 8, badgeWidth, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(categoryName.toUpperCase(), margin + 12, yPos - 2);
    yPos += 15;

    // Details
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    doc.setFont('helvetica', 'bold');
    doc.text('Ticket:', margin + 10, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(ticket.ticketNumber, margin + 35, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Customer:', margin + 10, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(ticket.customerName, margin + 35, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', margin + 10, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(ticket.eventDate, margin + 30, yPos);
    yPos += 8;

    // QR Code
    try {
      const qrCodeDataURL = await QRCode.toDataURL(ticket.qrCode, {
        width: 60,
        margin: 1
      });

      const qrSize = 40;
      const qrX = (pageWidth - qrSize) / 2;
      const qrY = yPos + 10;

      doc.addImage(qrCodeDataURL, 'PNG', qrX, qrY, qrSize, qrSize);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  }

  const pdfBlob = doc.output('blob');
  return pdfBlob;
}

/**
 * Generate a unique ticket number
 */
export function generateTicketNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `TKT-${timestamp}-${random}`;
}

/**
 * Generate QR code data for ticket
 */
export function generateQRCodeData(ticketNumber: string, eventId: string): string {
  return JSON.stringify({
    ticketNumber,
    eventId,
    timestamp: Date.now()
  });
}




